// The stick figure's geometry: the kinematics that turn the proportions in
// data/figure.json and the gaits in data/animations.json into joint angles. This is
// the single source of truth for how big the figure is and where its feet are:
//
//   sprites.js  draws the figure from these numbers,
//   motion.js   stands it on platforms at the height these numbers put its feet,
//   main.js     paces its gait at the stride these numbers can reach.
//
// So retuning a limb — longer legs, say — moves the drawn figure, its contact with
// the ground, AND its step length together, instead of drifting out of sync with a
// magic number somewhere else.
//
// It's a FACTORY, not a bag of constants, because the tools (meta/tasks.yaml) need
// to preview and validate edited values live, before they're written back to data/.
// The game uses the default instance, built from the data files.
//
// Units: fractions of the entity's body radius, +y = down (canvas convention),
// origin = the entity's `pos`. Angles: absolute, from straight-down, +ve toward the
// facing direction.

import { GAITS, BLEND, STAND } from "./animations.js";
import FIGURE from "../../data/figure.json" with { type: "json" };

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Build a figure from a set of proportions + the gaits it can move with.
 *
 * `stick` (data/figure.json) — each a fraction of body radius:
 *   LINE_W       stroke thickness of every limb
 *   HEAD_R       head radius            HEAD_Y      head centre (above origin)
 *   HIP_Y        hips / bottom of body  HIP_DX      half the gap between hips
 *   THIGH_LEN    hip → knee             SHIN_LEN    knee → foot
 *   KNEE_R       knee joint dot radius
 *   UPPER_ARM_LEN shoulder → elbow      FOREARM_LEN elbow → hand
 *   ELBOW_R      elbow joint dot radius
 *
 *   SHOULDER_Y   Where both arms attach to the body line, measured from the origin —
 *                so it must sit between NECK_Y (= HEAD_Y + HEAD_R) and HIP_Y to be on
 *                the body at all. Keep it clear of the head: the head is a stroked
 *                circle drawn LAST, over the arms, and its outer edge reaches
 *                HEAD_Y + HEAD_R + LINE_W/2. Attach the arms within a hair of that and
 *                they read as sprouting from the head rather than branching off the
 *                body, because the neck between the two is too short to see. The gap
 *                between that edge and SHOULDER_Y is the visible neck.
 *
 *   Both arms share one length, because a figure with one arm longer than the other
 *   is a figure with a deformity, not a style. They differ in how they're posed, not
 *   in how they're built: the aiming arm is solved to reach the cursor, the free arm
 *   is swung by the animation.
 *
 *   ARM_REACH    How far the AIMING arm extends toward the cursor, as a fraction of
 *                its full span — the arm's equivalent of RIDE. Below 1 so the elbow
 *                is actually bent and the two segments read as an arm rather than one
 *                straight stick. Only applies when aiming level; see aimArm().
 *   RIDE         How far the legs extend when carrying the figure, as a fraction of
 *                their full reach. NOT 1.0 on purpose: a leg locked straight can
 *                only touch the ground directly below the hip, so a figure at full
 *                extension physically cannot take a step. The slack is the figure's
 *                suspension — it buys the knee bend AND the stride length, and it's
 *                what limits how long a stride the run can have.
 *
 * `anims` (data/animations.json) — `gaits.walk`, `gaits.run`, `blend`, `stand`.
 */
export function makeFigure(stick, anims) {
    const gaits = anims.gaits, blend = anims.blend, stand = anims.stand;

    const REACH = stick.THIGH_LEN + stick.SHIN_LEN; // fully-extended leg
    const HIP_TO_FOOT = REACH * stick.RIDE;         // hips ride this far above the feet

    // Origin → ground contact: where the feet are, so where the
    // platform surface must be. Independent of gait — the figure
    // rides at one height whether it walks or runs, which is why
    // motion.js can stand it on a platform without knowing either.
    const FOOT = stick.HIP_Y + HIP_TO_FOOT;

    // The furthest a foot can reach forward/back while still touching
    // the ground at ride height (Pythagoras against the leg's full
    // reach). This is the hard cap on stride, and the reason a run
    // needs a lower RIDE than a walk: to step further you must ride
    // lower.
    const MAX_REACH_X = Math.sqrt(Math.max(0, REACH * REACH - HIP_TO_FOOT * HIP_TO_FOOT));

    // Two-segment limb IK: given a tip (foot/hand) position relative to the root
    // (hip/shoulder), find the angles of the two segments that put it there. Law of
    // cosines. A two-segment limb always has two mirror solutions — the middle joint
    // ahead of the root→tip line, or behind it — and `bend` picks which:
    //
    //   +1  middle joint AHEAD of the line: what a knee does (see THE KNEE RULE).
    //   -1  middle joint BEHIND the line: what an elbow does.
    //
    // That sign is the entire difference between a leg and an arm, so each limb gets
    // its joint's rule by construction and IK can't produce a limb that bends wrong.
    function limbIK(x, y, L1, L2, bend) {
        const d = Math.min(Math.hypot(x, y), L1 + L2 - 1e-4); // never fully locked
        const dir = Math.atan2(x, y);                         // root → tip
        const cos = (d * d + L1 * L1 - L2 * L2) / (2 * d * L1);
        const off = Math.acos(clamp(cos, -1, 1));             // upper segment's offset from that line
        const upper = dir + bend * off;
        const lower = Math.atan2(x - Math.sin(upper) * L1, y - Math.cos(upper) * L1);
        return { upper, lower };
    }

    // A leg. Knee-forward always, so the heel trails: shin <= thigh,
    // by construction.
    function legIK(x, y) {
        const { upper, lower } = limbIK(x, y, stick.THIGH_LEN, stick.SHIN_LEN, +1);
        return { thigh: upper, shin: lower };
    }

    // --- arms -------------------------------------------------------------------------
    const ARM_SPAN = stick.UPPER_ARM_LEN + stick.FOREARM_LEN; // fully-extended arm

    // How far below the shoulder the elbow ends up (+y = down), for
    // picking between the two IK solutions.
    const elbowDrop = (p) => Math.cos(p.upper) * stick.UPPER_ARM_LEN;

    // The AIMING arm: the hand tracks the cursor, the elbow is
    // solved.
    //
    // Unlike a hip, a shoulder swings through a full circle — you can
    // aim behind you — so there's no fixed "elbows bend this way
    // relative to the body" rule to apply. The rule that actually
    // makes an aiming arm read right is gravity: the elbow hangs
    // BELOW the shoulder→hand line. So we solve both ways and keep
    // the lower elbow.
    //
    // That choice flips as the aim crosses vertical, which would pop
    // the elbow from one side to the other. It doesn't, because the
    // arm also straightens as the aim approaches vertical (reaching
    // straight up, your arm IS straight) — at the crossing the bend
    // is ~0, both solutions coincide, and the swap is invisible.
    // Aiming horizontally, where the elbow is most visible, it stays
    // bent at ARM_REACH.
    //
    // `x` is in the FACING frame (the renderer mirrors it back), but
    // the elbow-drop test only looks at y, which mirroring doesn't
    // touch — so the choice is the same either way the figure faces.
    function aimArm(aimAngle, sgn = 1) {
        const horiz = Math.abs(Math.cos(aimAngle));             // 1 = level, 0 = straight up/down
        const reach = lerp(1, stick.ARM_REACH, horiz) * ARM_SPAN;
        return armTo(sgn * Math.cos(aimAngle) * reach, Math.sin(aimAngle) * reach);
    }

    // Put a hand at a specific point (relative to the shoulder,
    // facing frame) rather than in a direction — that's what a second
    // hand gripping a weapon needs: the grip is somewhere in
    // particular, not merely over that way.
    //
    // Same elbow-hangs-lower rule as aimArm. If the point is out of
    // reach, limbIK clamps and the arm stretches toward it as far as
    // it goes, which is why weapons put the off-hand grip BEHIND the
    // main hand (see sprites.js) — a grip further out than the main
    // hand can't be reached by an arm of the same length from the
    // same shoulder, and the hand would visibly fall short of its own
    // weapon.
    function armTo(x, y) {
        const down = limbIK(x, y, stick.UPPER_ARM_LEN, stick.FOREARM_LEN, +1);
        const up = limbIK(x, y, stick.UPPER_ARM_LEN, stick.FOREARM_LEN, -1);
        const p = elbowDrop(down) >= elbowDrop(up) ? down : up;
        return { upper: p.upper, fore: p.lower };
    }

    // Where a hand ends up, given its pose — the renderer needs this
    // to hang a weapon off it, and it's the same forward kinematics
    // sprites.js draws with.
    const handAt = (arm, sgn) => ({
        x: sgn * (Math.sin(arm.upper) * stick.UPPER_ARM_LEN + Math.sin(arm.fore) * stick.FOREARM_LEN),
        y: stick.SHOULDER_Y + Math.cos(arm.upper) * stick.UPPER_ARM_LEN + Math.cos(arm.fore) * stick.FOREARM_LEN,
    });

    // A SWINGING arm: authored, not solved. The animation gives the
    // shoulder angle; the elbow carries a fixed flex (`armBend`) that
    // the gait blend interpolates — a walk's arm is nearly straight,
    // a run's is folded near 90°, and that fold is a lot of what
    // reads as "running". Positive bend puts the forearm AHEAD of the
    // upper arm, which is the only way an elbow goes.
    const swingArm = (shoulder, bendAmt) => ({ upper: shoulder, fore: shoulder + bendAmt });

    // --- the rest stance ---------------------------------------------------------------
    // Standing is a FRONT view: limbs splay left/right, symmetrically. Walking is a SIDE
    // view: limbs swing fore/aft. The figure is a hybrid (note the face is drawn front-on
    // while the legs stride sideways), and the two views don't share an axis convention.
    //
    // Everything else here is in the FACING frame, where +x is the way the figure walks
    // and the renderer mirrors x by `sgn`. A left/right splay must NOT mirror — mirroring
    // it would swing each foot across to the other side and cross the legs. But the hips
    // are pinned at ±HIP_DX and never mirror, so the crossing would be visible.
    //
    // So the stance is authored in SCREEN space and multiplied by `sgn` on the way in:
    // the renderer's mirror then cancels it back out, leaving the stance facing-neutral.
    // `side` is -1 for the figure's left limb, +1 for its right.
    // The feet plant `legAngle` out from vertical, at the same ride height the physics
    // stands the figure at — so the stance splays without lifting it off the platform.
    //
    // The knees bow OUTWARD, mirrored, rather than both bending toward +facing like a
    // walking leg's. RIDE keeps the legs ~10% short of straight (it's what buys the
    // stride), so that bend has to go somewhere, and sending both knees the same way
    // makes one leg bow in and the other out — visibly lopsided in a pose whose whole job
    // is to be symmetric. A front view has no fore/aft, so which way a knee bends is a
    // free choice here, and outward is what the reference does.
    const restLeg = (side, sgn) => {
        const x = sgn * side * HIP_TO_FOOT * Math.tan(stand.legAngle);
        const { upper, lower } = limbIK(x, HIP_TO_FOOT, stick.THIGH_LEN, stick.SHIN_LEN, sgn * side);
        return { thigh: upper, shin: lower };
    };

    // Both arms hang down-and-out at `armAngle`, forearms folding
    // back INWARD toward the body's midline by `armBend`. Inward is
    // +x for the left arm and -x for the right, so the fold is
    // mirrored — which is why this can't reuse the facing-frame elbow
    // rule (that rule is about fore/aft, and this pose has no
    // fore/aft component at all).
    const restArm = (side, sgn) => ({
        upper: sgn * side * stand.armAngle,
        fore: sgn * side * (stand.armAngle - stand.armBend),
    });

    // --- solving a gait --------------------------------------------------------------
    // A gait is `{ duty, stride, armSwing, swing[] }`. Only the SWING leg is authored:
    // the planted leg is solved, so its foot stays welded to the ground while the hips
    // travel over it. (A hand-authored stance foot slides — that's what "not really
    // walking" looks like.)
    function solveGait(g) {
        // The authored stride, CLAMPED to what the legs can actually
        // reach: a stride longer than the leg can span would tear the
        // planted foot off the ground at the ends of the stance. Body
        // advance per stance = stride * duty, so the foot swings
        // ±(stride * duty / 2) about the hip; keep a 5% margin off
        // the fully-locked leg.
        const reachable = (2 * MAX_REACH_X * 0.95) / g.duty;
        const STRIDE = Math.min(g.stride, reachable);
        const HALF_STEP = (STRIDE * g.duty) / 2;

        // The stance poses the swing must leave from and land on, so
        // the airborne leg meets the planted leg exactly, with no
        // pop.
        const TOE_OFF = legIK(-HALF_STEP, HIP_TO_FOOT);
        const CONTACT = legIK(HALF_STEP, HIP_TO_FOOT);
        const SWING = [{ u: 0, ...TOE_OFF }, ...g.swing, { u: 1, ...CONTACT }];

        function swingLeg(u) {
            let a = SWING[0], b = SWING[SWING.length - 1];
            for (let i = 0; i < SWING.length - 1; i++) {
                if (u >= SWING[i].u && u <= SWING[i + 1].u) { a = SWING[i]; b = SWING[i + 1]; break; }
            }
            const t = b.u > a.u ? (u - a.u) / (b.u - a.u) : 0;
            return { thigh: lerp(a.thigh, b.thigh, t), shin: lerp(a.shin, b.shin, t) };
        }

        // One leg at phase p: planted for the first `duty` of the
        // cycle, airborne after.
        function legAt(p) {
            const phase = ((p % 1) + 1) % 1;
            if (phase < g.duty) {
                // Stance. The foot does not move over the ground —
                // the body moves over the foot — so relative to the
                // hip it slides straight back, at ride height, from
                // one half-step forward to one half-step back. Solve,
                // don't animate.
                const u = phase / g.duty;
                return legIK(HALF_STEP - u * (2 * HALF_STEP), HIP_TO_FOOT);
            }
            return swingLeg((phase - g.duty) / (1 - g.duty));
        }

        return { ...g, STRIDE, HALF_STEP, reachable, legAt };
    }

    // --- blending walk → run ----------------------------------------------------------
    // We interpolate the gait's PARAMETERS and re-solve, rather than cross-fading two
    // solved cycles' poses. It matters: a pose cross-fade has the foot planted in one
    // cycle and airborne in the other at the same instant, so the planted foot slides at
    // every intermediate blend — and derp-runner lives permanently in between. Blending
    // the parameters yields a gait that is itself a coherent cycle, with its stance
    // solved by IK, so the foot stays welded at EVERY blend value, not just the ends.
    //
    // The two cycles must be keyed at the same `u` values for this to be meaningful;
    // validate() enforces it.
    function blendParams(a, b, t) {
        return {
            duty: lerp(a.duty, b.duty, t),
            stride: lerp(a.stride, b.stride, t),
            armSwing: lerp(a.armSwing, b.armSwing, t),
            armBend: lerp(a.armBend, b.armBend, t),
            swing: a.swing.map((k, i) => ({
                u: k.u,
                thigh: lerp(k.thigh, b.swing[i].thigh, t),
                shin: lerp(k.shin, b.swing[i].shin, t),
            })),
        };
    }

    const WALK = solveGait(gaits.walk);
    const RUN = solveGait(gaits.run);
    const cache = new Map(); // quantized blend → solved gait; re-solving every frame is wasteful

    function gaitAt(t) {
        const b = clamp(t, 0, 1);
        if (b <= 0.001) return WALK;
        if (b >= 0.999) return RUN;
        const key = Math.round(b * 50) / 50; // 2% steps — finer than the eye can see here
        let g = cache.get(key);
        if (!g) { g = solveGait(blendParams(gaits.walk, gaits.run, key)); cache.set(key, g); }
        return g;
    }

    // How much of a run this entity is doing, 0..1, from its speed.
    //
    // Speed is measured in BODY RADII per second, not pixels: a big
    // figure moving at 300px/s is strolling where a small one is
    // sprinting, and the gait should follow the figure, not the
    // screen. (This is why the boss doesn't sprint and the derp
    // does.)
    function gaitBlend(speedPx, radius, sc = 1) {
        const r = radius * (sc || 1);
        if (!(r > 0)) return 0;
        const radiiPerSec = Math.abs(speedPx) / r;
        return clamp((radiiPerSec - blend.walkBelow) / (blend.runAbove - blend.walkBelow), 0, 1);
    }

    const blendLeg = (a, b, w) => ({ thigh: lerp(a.thigh, b.thigh, w), shin: lerp(a.shin, b.shin, w) });
    const blendArm = (a, b, w) => ({ upper: lerp(a.upper, b.upper, w), fore: lerp(a.fore, b.fore, w) });

    // The pose to draw: `{ legs: {left, right}, arms: {left, right}
    // }`, every limb two segments. Arms are left/right rather than
    // aim/free because a weapon decides which arms it occupies — a
    // one-handed weapon leaves one arm free to swing, a two-handed
    // one takes both — so the caller composes, and this only supplies
    // the arms nothing else has claimed.
    //
    // `moveAmount` is how much the figure is MOVING, 0..1 — not a
    // boolean. A figure that snaps between standing and mid-stride
    // the instant it crosses some speed threshold jitters, because
    // the threshold sits in the noise of a position stream. Blending
    // across the transition lets the limbs ease into the cycle
    // instead. It's also what carries the figure between the stance's
    // front view and the gait's side view.
    //
    // `gait` is how much of that movement is a run, 0..1 (see
    // gaitBlend). `sgn` is the facing (+1 right, -1 left) — the
    // stance needs it; see restLeg.
    function poseAt(phase, moveAmount = 0, gait = 0, sgn = 1) {
        const rest = {
            legs: { left: restLeg(-1, sgn), right: restLeg(+1, sgn) },
            arms: { left: restArm(-1, sgn), right: restArm(+1, sgn) },
        };
        const w = clamp(moveAmount, 0, 1);
        if (w === 0) return rest;

        const g = gaitAt(gait);
        // Arms counter-swing against the opposite leg — left arm back
        // as the left leg comes forward. That opposition is what
        // stops a walk looking like a shamble.
        const swing = g.armSwing * Math.cos(2 * Math.PI * phase);
        const moving = {
            legs: { left: g.legAt(phase), right: g.legAt(phase + 0.5) },
            arms: { left: swingArm(-swing, g.armBend), right: swingArm(swing, g.armBend) },
        };
        return {
            legs: {
                left: blendLeg(rest.legs.left, moving.legs.left, w),
                right: blendLeg(rest.legs.right, moving.legs.right, w),
            },
            arms: {
                left: blendArm(rest.arms.left, moving.arms.left, w),
                right: blendArm(rest.arms.right, moving.arms.right, w),
            },
        };
    }

    // Does this describe a body that can actually stand and move?
    // Returns a list of problems, empty if sound. The tools call it
    // on every edit (so a bad figure can't be saved) and `npm run
    // check` calls it on the committed data.
    function validate() {
        const bad = [];
        for (const [k, v] of Object.entries(stick)) {
            if (typeof v !== "number" || !Number.isFinite(v)) bad.push(`proportion ${k} is not a number: ${v}`);
        }
        if (stick.RIDE >= 1) bad.push(`RIDE must be < 1: legs locked straight cannot take a step (got ${stick.RIDE})`);
        if (stick.RIDE <= 0.5) bad.push(`RIDE ${stick.RIDE} is a deep crouch; expected ~0.9`);

        // The arms have to attach to the body, and be seen doing it.
        // The head is stroked over the top of them, so a shoulder
        // tucked under its edge leaves the arms looking like they
        // sprout from the head — the neck has to be at least half a
        // stroke wide to register as a neck at all.
        const neckY = stick.HEAD_Y + stick.HEAD_R;
        const headEdge = neckY + stick.LINE_W / 2;
        if (stick.SHOULDER_Y > stick.HIP_Y) bad.push(`SHOULDER_Y ${stick.SHOULDER_Y} is below HIP_Y ${stick.HIP_Y} — the arms would hang off the legs, not the body`);
        if (stick.SHOULDER_Y < neckY) bad.push(`SHOULDER_Y ${stick.SHOULDER_Y} is above NECK_Y ${neckY.toFixed(2)} — the arms would attach above the body line, to nothing`);
        else if (stick.SHOULDER_Y - headEdge < stick.LINE_W / 2) {
            bad.push(`SHOULDER_Y ${stick.SHOULDER_Y} leaves only ${(stick.SHOULDER_Y - headEdge).toFixed(3)}r of neck below the head's edge (${headEdge.toFixed(3)}r) — the arms will read as sprouting from the head. Lower the shoulders or raise the head.`);
        }

        const walkKeys = gaits.walk.swing.map((k) => k.u).join(",");
        const runKeys = gaits.run.swing.map((k) => k.u).join(",");
        if (walkKeys !== runKeys) {
            bad.push(`walk and run must be keyed at the same u values to blend (walk: ${walkKeys} / run: ${runKeys})`);
            return bad; // blending is meaningless until they line up; further checks would mislead
        }

        for (const [name, g] of Object.entries(gaits)) {
            if (g.duty <= 0 || g.duty >= 1) bad.push(`${name} duty must be in (0,1): got ${g.duty}`);
            // THE ELBOW RULE: an elbow only bends forwards — the
            // forearm leads the upper arm. A negative bend is a
            // backwards-breaking elbow.
            if (g.armBend < 0) bad.push(`${name} armBend must be >= 0: an elbow doesn't bend backwards (got ${g.armBend})`);
            const solved = solveGait(g);
            if (g.stride > solved.reachable + 1e-9) {
                bad.push(`${name} stride ${g.stride} exceeds what these legs can reach at RIDE ${stick.RIDE} (max ${solved.reachable.toFixed(2)}) — it will be clamped, so steps won't grow`);
            }
            for (const k of g.swing) {
                if (k.shin > k.thigh) bad.push(`${name} swing keyframe u=${k.u}: inverted knee (shin ${k.shin} > thigh ${k.thigh})`);
            }
        }

        // The rest stance is a front view, so it has no fore/aft
        // component for the knee and elbow rules to talk about —
        // they're side-view rules. What it must not do is splay so
        // far the legs can't reach the ground they're standing on.
        if (stand.legAngle < 0) bad.push(`stand legAngle must be >= 0 (got ${stand.legAngle}) — it's a splay, and it mirrors`);
        if (stand.armBend < 0) bad.push(`stand armBend must be >= 0: an elbow doesn't bend backwards (got ${stand.armBend})`);
        const restReach = Math.hypot(HIP_TO_FOOT * Math.tan(stand.legAngle), HIP_TO_FOOT);
        if (restReach > REACH - 1e-4) {
            bad.push(`stand legAngle ${stand.legAngle} splays the feet ${restReach.toFixed(2)} from the hips, past what the legs can reach (${REACH.toFixed(2)}) — the figure can't stand like that`);
        }

        // THE KNEE RULE + THE ELBOW RULE across every gait the figure
        // can be in — including the blends, not just walk and run.
        // Interpolating between two legal poses can still pass
        // through an illegal one. Checked at moveAmount 1 (pure
        // side-view gait), since that's the only place these rules
        // apply.
        for (const t of [0, 0.25, 0.5, 0.75, 1]) {
            for (let i = 0; i < 180; i++) {
                const pose = poseAt(i / 180, 1, t);
                const at = `gait blend ${t} at phase ${(i / 180).toFixed(2)}`;
                const knee = ["left", "right"].find((s) => pose.legs[s].shin > pose.legs[s].thigh + 1e-9);
                if (knee) { bad.push(`${at}: inverts the ${knee} knee`); break; }
                const elbow = ["left", "right"].find((s) => pose.arms[s].fore < pose.arms[s].upper - 1e-9);
                if (elbow) { bad.push(`${at}: inverts the ${elbow} elbow`); break; }
            }
        }

        // The aiming arm picks its elbow side from which way is DOWN,
        // and that choice flips as the aim crosses vertical. The arm
        // straightening near vertical is what keeps that flip
        // invisible — so check the elbow actually travels
        // continuously all the way around, rather than trusting that
        // it does.
        for (const sgn of [1, -1]) {
            let prev = null, worst = 0, worstAt = 0;
            for (let i = 0; i <= 720; i++) {
                const a = (i / 720) * Math.PI * 2 - Math.PI;
                const p = aimArm(a, sgn);
                const e = { x: sgn * Math.sin(p.upper) * stick.UPPER_ARM_LEN, y: Math.cos(p.upper) * stick.UPPER_ARM_LEN };
                if (prev) {
                    const step = Math.hypot(e.x - prev.x, e.y - prev.y);
                    if (step > worst) { worst = step; worstAt = a; }
                }
                prev = e;
            }
            // 0.5° of aim should never move the elbow more than a
            // hair. An un-straightened side-flip shows up here as ~2
            // * UPPER_ARM_LEN (≈1.2r) — this catches that with an
            // order of magnitude to spare. It can't be zero: a 2D
            // two-segment limb has two discrete solutions, so the
            // swap is only ever hidden, never removed. What's left is
            // the arm's residual bend at full extension, ~0.04r ≈
            // half a pixel.
            if (worst > 0.1) {
                bad.push(`aiming arm's elbow jumps ${worst.toFixed(3)}r at aim ${worstAt.toFixed(2)}rad (facing ${sgn > 0 ? "right" : "left"}) — it should sweep smoothly`);
            }
        }
        return bad;
    }

    return {
        STICK: stick,
        FOOT,          // origin → ground, in body radii (gait-independent)
        HIP_TO_FOOT,
        MAX_REACH_X,
        ARM_SPAN,
        WALK,
        RUN,
        legIK,
        aimArm,
        armTo,
        handAt,
        gaitAt,
        gaitBlend,
        poseAt,
        validate,

        // Ground covered per cycle, in px, at a given walk↔run blend.
        // Bigger figures take proportionally bigger steps, so their
        // legs cycle slower — cadence falls out of the geometry
        // instead of being tuned per enemy.
        strideLength: (radius, sc = 1, gait = 0) => radius * (sc || 1) * gaitAt(gait).STRIDE,

        // Origin → ground contact, in px, for one entity.
        // `sprite.scale` scales the whole drawn figure (derp-runner
        // is 0.9), so the contact point scales with it. Shapes
        // without legs have no stance to stand in — their body radius
        // is the best available contact point.
        footOffset: (sprite, radius) => {
            if (!sprite || sprite.shape !== "stick") return radius;
            return radius * (sprite.scale || 1) * FOOT;
        },
    };
}

// The figure the GAME uses, built from the data files. Tools build
// their own from edited values and preview those, without touching
// this one.
export const FIGURE_DEFAULT = makeFigure(FIGURE.stick, { gaits: GAITS, blend: BLEND, stand: STAND });

export const STICK = FIGURE_DEFAULT.STICK;
export const STICK_FOOT = FIGURE_DEFAULT.FOOT;
export const poseAt = FIGURE_DEFAULT.poseAt;
export const footOffset = FIGURE_DEFAULT.footOffset;
export const strideLength = FIGURE_DEFAULT.strideLength;
export const gaitBlend = FIGURE_DEFAULT.gaitBlend;
